/**
 * Position Normalization
 *
 * Functions for normalizing position units (cm to meters, etc.)
 */

import {Position} from "../../types/position";

/**
 * Normalize position units to meters
 * Detects if values are likely in centimeters and converts
 *
 * @param position Position to normalize
 * @param unit Force unit interpretation: 'auto' | 'meters' | 'centimeters'
 * @returns Position in meters
 */
export function normalizePositionUnits(
  position: Position,
  unit: "auto" | "meters" | "centimeters" = "auto"
): Position {
  if (unit === "meters") {
    return {...position};
  }

  if (unit === "centimeters") {
    return {
      x: position.x / 100,
      y: position.y / 100,
      z: position.z / 100,
    };
  }

  // Auto-detect: if any axis > 50, likely centimeters
  const maxAxis = Math.max(
    Math.abs(position.x),
    Math.abs(position.y),
    Math.abs(position.z)
  );

  if (maxAxis > 50) {
    return {
      x: position.x / 100,
      y: position.y / 100,
      z: position.z / 100,
    };
  }

  return {...position};
}

/**
 * Convert meters to centimeters
 *
 * @param position Position in meters
 * @returns Position in centimeters
 */
export function metersToCentimeters(position: Position): Position {
  return {
    x: position.x * 100,
    y: position.y * 100,
    z: position.z * 100,
  };
}

/**
 * Convert centimeters to meters
 *
 * @param position Position in centimeters
 * @returns Position in meters
 */
export function centimetersToMeters(position: Position): Position {
  return {
    x: position.x / 100,
    y: position.y / 100,
    z: position.z / 100,
  };
}

/**
 * Check if position values are likely in centimeters
 *
 * @param position Position to check
 * @param threshold Threshold for detection (default: 50)
 * @returns True if likely centimeters
 */
export function isLikelyCentimeters(
  position: Position,
  threshold: number = 50
): boolean {
  const maxAxis = Math.max(
    Math.abs(position.x),
    Math.abs(position.y),
    Math.abs(position.z)
  );
  return maxAxis > threshold;
}
